import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { api, go, projectMeta, communityOpen } from './util';
const lines = s => String(s || '').split('\n').map(x => x.trim()).filter(Boolean);

/**
 * /projects/new and /projects/{slug}?edit=1: a project's details. First its type: a community project (in a Nigerian
 * state, started with a second person: a Voucha username, or an email for someone not on Voucha yet) or a personal one
 * (yours alone, private unless you make it public, anywhere). Either can start from a planning template (its
 * milestones and typical requirements, with the Data Hub's prices for the state in Nigeria). Budget, funding target and
 * date, and (personal) savings reminders. Milestones, needs and money are managed on the project's page.
 */
export default class ProjectForm extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.slug = this.attrs.slug || null;
    const personal = m.route.param('type') === 'personal' || !communityOpen();
    this.f = {
      type: personal ? 'personal' : 'community',
      template: '',
      country: 'NG',
      kind: personal ? 'fundraising' : 'both',
      status: 'planning',
      currency: 'NGN',
      stateId: '',
      lgaId: '',
      partnerRole: 'owner',
      public: false
    };
    this.milestones = '';
    this.lgas = [];
    this.error = null;
    this.saving = false;
    this.loaded = !this.slug;
    if (this.slug) {
      app.request({
        method: 'GET',
        url: api('/projects/' + this.slug),
        errorHandler: () => {}
      }).then(r => {
        const p = r.project;
        this.p = p;
        this.f = {
          name: p.name,
          summary: p.summary || '',
          description: p.description || '',
          objectives: (p.objectives || []).join('\n'),
          outcomes: (p.outcomes || []).join('\n'),
          stateId: String(p.stateId),
          lgaId: p.lgaId ? String(p.lgaId) : '',
          location: p.location || '',
          industry: p.industryKey || '',
          kind: p.kind,
          status: p.status,
          startDate: p.startDate || '',
          endDate: p.endDate || '',
          currency: p.currency,
          type: p.type,
          country: p.country || 'NG',
          place: p.place || '',
          public: p.visibility === 'public',
          budget: p.budget || '',
          fundingTarget: p.fundingTarget || '',
          fundingBy: p.fundingBy || '',
          reminderEvery: p.reminderEvery || ''
        };
        this.loadLgas();
      }).catch(() => this.error = 'This project could not be found.').then(() => {
        this.loaded = true;
        m.redraw();
      });
    } else {
      // started from a state's page (?state=kogi)
      const want = m.route.param('state');
      if (want) {
        const tryState = () => {
          const s = ((projectMeta() || {}).states || []).find(x => x.slug === want);
          if (s) {
            this.f.stateId = String(s.id);
            this.loadLgas();
          } else if (!projectMeta()) setTimeout(tryState, 300);
        };
        tryState();
      }
    }
  }
  loadLgas() {
    this.lgas = [];
    if (!this.f.stateId) return;
    app.request({
      method: 'GET',
      url: api('/states/' + this.f.stateId + '/lgas'),
      errorHandler: () => {}
    }).then(r => this.lgas = r && r.lgas || []).catch(() => {}).then(() => m.redraw());
  }
  save() {
    this.saving = true;
    this.error = null;
    const body = Object.assign({}, this.f, {
      objectives: lines(this.f.objectives),
      outcomes: lines(this.f.outcomes)
    });
    if (this.f.country !== 'NG') body.stateId = '';
    if (!this.slug) body.milestones = lines(this.milestones).map(title => ({
      title
    }));
    app.request({
      method: 'POST',
      url: api('/projects' + (this.p ? '/' + this.p.id : '')),
      body,
      errorHandler: () => {}
    }).then(r => {
      app.alerts.show({
        type: 'success'
      }, this.p ? 'Saved.' : this.f.type === 'personal' ? 'Project started.' : 'Project started. It goes public once the second person joins.');
      m.route.set('/projects/' + r.project.slug);
    }).catch(e => {
      this.error = e && e.response && e.response.message || 'That didn’t save. Please try again.';
      this.saving = false;
      m.redraw();
    });
  }
  view() {
    if (!app.session.user) {
      return m("div", {
        className: "VEvt VEvt-empty"
      }, m("i", {
        className: "fas fa-people-carry-box",
        "aria-hidden": "true"
      }), m("h3", null, "Log in to start a project"));
    }
    if (!this.loaded) return m(LoadingIndicator, null);
    if (this.error && this.slug && !this.p) return m("p", {
      className: "VBiz-error"
    }, this.error);
    const meta = projectMeta() || {};
    const f = this.f;
    const bind = k => ({
      value: f[k] || '',
      oninput: e => f[k] = e.target.value
    });
    const opts = list => (list || []).map(o => m("option", {
      value: o.key
    }, o.name));
    const personal = f.type === 'personal';
    const nigeria = f.country === 'NG';
    const pick = (k, v) => () => f[k] = v;
    return m("form", {
      className: "VProj-form",
      onsubmit: e => {
        e.preventDefault();
        this.save();
      }
    }, m("a", {
      className: "VProj-back",
      href: this.p ? '/projects/' + this.p.slug : '/projects',
      onclick: go(this.p ? '/projects/' + this.p.slug : '/projects')
    }, "\u2190 ", this.p ? this.p.name : 'Projects'), m("h2", null, this.p ? 'Edit the project' : 'Start a project'), !this.p ? m("div", {
      className: "VProj-types",
      role: "radiogroup"
    }, communityOpen() ? m("button", {
      type: "button",
      role: "radio",
      "aria-checked": !personal ? 'true' : 'false',
      className: 'VProj-type' + (!personal ? ' is-on' : ''),
      onclick: () => Object.assign(f, {
        type: 'community',
        country: 'NG',
        kind: 'both'
      })
    }, m("i", {
      className: "fas fa-people-group",
      "aria-hidden": "true"
    }), m("strong", null, "Community project"), m("span", null, "With others, in a Nigerian state: needs two people to start; people can follow, vouch and join in.")) : m("button", {
      type: "button",
      role: "radio",
      "aria-checked": "false",
      "aria-disabled": "true",
      disabled: true,
      className: "VProj-type is-soon"
    }, m("i", {
      className: "fas fa-people-group",
      "aria-hidden": "true"
    }), m("strong", null, "Community project ", m("em", {
      className: "VProj-soonTag"
    }, "Coming soon")), m("span", null, "Projects with others in your state, which people can follow, vouch for and join. Not open yet.")), m("button", {
      type: "button",
      role: "radio",
      "aria-checked": personal ? 'true' : 'false',
      className: 'VProj-type' + (personal ? ' is-on' : ''),
      onclick: () => Object.assign(f, {
        type: 'personal',
        kind: 'fundraising'
      })
    }, m("i", {
      className: "fas fa-user-lock",
      "aria-hidden": "true"
    }), m("strong", null, "Personal project"), m("span", null, "Yours: a house, land, a business. Private unless you make it public; share it with the people you choose."))) : null, !this.p ? m("fieldset", null, m("legend", null, "Start from a template"), m("p", {
      className: "VProj-hint"
    }, "A starting list of milestones and the usual requirements. ", nigeria ? 'In Nigeria they come with the Data Hub’s typical prices for the state, where we have them. ' : '', "Change, add or remove anything afterwards."), m("div", {
      className: "VProj-templates"
    }, m("button", {
      type: "button",
      className: 'VProj-tpl' + (!f.template ? ' is-on' : ''),
      onclick: pick('template', '')
    }, m("strong", null, "Blank"), m("span", null, "Start from nothing")), (meta.templates || []).map(t => m("button", {
      type: "button",
      className: 'VProj-tpl' + (f.template === t.key ? ' is-on' : ''),
      onclick: pick('template', t.key)
    }, m("strong", null, t.name), m("span", null, t.about))))) : null, m("fieldset", null, m("legend", null, "What it is"), m("label", null, "Project name", m("input", Object.assign({
      className: "FormControl",
      required: true,
      maxlength: "150",
      placeholder: "e.g. Adankolo market solar borehole"
    }, bind('name')))), m("label", null, "In one line", m("input", Object.assign({
      className: "FormControl",
      maxlength: "300",
      placeholder: "Clean water for 2,000 traders"
    }, bind('summary')))), m("label", null, "Description", personal ? ' (optional)' : '', m("textarea", Object.assign({
      className: "FormControl",
      rows: "5",
      maxlength: "5000",
      placeholder: personal ? 'What it is and what you want from it' : 'What it is, who it helps and why it matters'
    }, bind('description')))), m("div", {
      className: "VProj-grid"
    }, m("label", null, "Kind", m("select", Object.assign({
      className: "FormControl"
    }, bind('kind')), opts(meta.kinds))), m("label", null, "Industry", m("select", Object.assign({
      className: "FormControl"
    }, bind('industry')), m("option", {
      value: ""
    }, "Choose\u2026"), opts(meta.industries)))), m("p", {
      className: "VProj-hint"
    }, "Fundraising: it needs money or materials. Monitoring: people watch over something being built or run (a government road, a school). Both: either.")), m("fieldset", null, m("legend", null, "Where"), personal ? m("label", null, "Country", m("select", {
      className: "FormControl",
      value: f.country,
      onchange: e => f.country = e.target.value
    }, m("option", {
      value: "NG"
    }, "Nigeria"), (meta.countries || []).filter(c => c.key !== 'NG').map(c => m("option", {
      value: c.key
    }, c.name)))) : null, !nigeria ? m("label", null, "Town or city", m("input", Object.assign({
      className: "FormControl",
      maxlength: "150",
      placeholder: "e.g. Toronto"
    }, bind('place'))), m("span", {
      className: "VProj-hint"
    }, "Reference prices and business recommendations are for projects in Nigeria.")) : null, m("div", {
      className: "VProj-grid",
      style: nigeria ? '' : 'display:none'
    }, m("label", null, "State", m("select", {
      className: "FormControl",
      required: nigeria,
      value: f.stateId,
      onchange: e => {
        f.stateId = e.target.value;
        f.lgaId = '';
        this.loadLgas();
      }
    }, m("option", {
      value: ""
    }, "Choose\u2026"), (meta.states || []).map(s => m("option", {
      value: String(s.id)
    }, s.name)))), m("label", null, "Local government area", m("select", Object.assign({
      className: "FormControl"
    }, bind('lgaId')), m("option", {
      value: ""
    }, "Whole state / not sure"), this.lgas.map(l => m("option", {
      value: String(l.id)
    }, l.name))))), nigeria ? m("label", null, "Location", m("input", Object.assign({
      className: "FormControl",
      maxlength: "150",
      placeholder: "Town, ward or site"
    }, bind('location')))) : null), m("fieldset", null, m("legend", null, "Goals and timeline"), m("label", null, "Objectives (one per line)", m("textarea", Object.assign({
      className: "FormControl",
      rows: "3"
    }, bind('objectives')))), m("label", null, "Expected outcomes (one per line)", m("textarea", Object.assign({
      className: "FormControl",
      rows: "3"
    }, bind('outcomes')))), m("div", {
      className: "VProj-grid"
    }, m("label", null, "Start date", m("input", Object.assign({
      className: "FormControl",
      type: "date"
    }, bind('startDate')))), m("label", null, "End date", m("input", Object.assign({
      className: "FormControl",
      type: "date"
    }, bind('endDate')))), m("label", null, "Status", m("select", Object.assign({
      className: "FormControl"
    }, bind('status')), opts(meta.statuses))), m("label", null, "Currency", m("select", Object.assign({
      className: "FormControl"
    }, bind('currency')), (meta.currencies || ['NGN']).map(c => m("option", {
      value: c
    }, c))))), !this.p && !f.template ? m("label", null, "First milestones (one per line; add targets, dates, needs and costs on the project\u2019s page)", m("textarea", {
      className: "FormControl",
      rows: "4",
      placeholder: 'Survey the site\nDrill the borehole\nInstall the pump and solar panels',
      value: this.milestones,
      oninput: e => this.milestones = e.target.value
    })) : null), m("fieldset", null, m("legend", null, "Money"), m("p", {
      className: "VProj-hint"
    }, "All optional. Without a budget or target, the project uses the total of what it needs."), m("div", {
      className: "VProj-grid"
    }, m("label", null, "Overall budget (", f.currency, ")", m("input", Object.assign({
      className: "FormControl",
      inputmode: "decimal",
      placeholder: "e.g. 25,000,000"
    }, bind('budget')))), m("label", null, "Funding target (", f.currency, ")", m("input", Object.assign({
      className: "FormControl",
      inputmode: "decimal",
      placeholder: "What you need to raise or save"
    }, bind('fundingTarget')))), m("label", null, "Needed by", m("input", Object.assign({
      className: "FormControl",
      type: "date"
    }, bind('fundingBy')))), personal ? m("label", null, "Savings reminder", m("select", Object.assign({
      className: "FormControl"
    }, bind('reminderEvery')), m("option", {
      value: ""
    }, "No reminder"), opts(meta.reminders))) : null), personal ? m("p", {
      className: "VProj-hint"
    }, "We\u2019ll remind you to check your savings until you reach the target, and two weeks before the date.") : null), personal ? m("fieldset", null, m("legend", null, "Who can see it"), m("label", {
      className: "VProj-check"
    }, m("input", {
      type: "checkbox",
      checked: !!f.public,
      onchange: e => f.public = e.target.checked
    }), " Make it public: anyone can see and follow it"), m("p", {
      className: "VProj-hint"
    }, "Private projects are seen only by you and the people you share them with (on the project\u2019s page, under People).")) : null, !this.p && !personal ? m("fieldset", null, m("legend", null, "Who you\u2019re starting it with"), m("p", {
      className: "VProj-hint"
    }, "Every project has at least two people. Their Voucha username, or their email if they aren\u2019t on Voucha yet (they\u2019ll get a link to join)."), m("div", {
      className: "VProj-grid"
    }, m("label", null, "Second person", m("input", Object.assign({
      className: "FormControl",
      required: !personal,
      placeholder: "@username or email"
    }, bind('partner')))), m("label", null, "As", m("select", Object.assign({
      className: "FormControl"
    }, bind('partnerRole')), m("option", {
      value: "owner"
    }, "Co-owner"), m("option", {
      value: "contributor"
    }, "Contributor"))))) : null, this.error ? m("p", {
      className: "VBiz-error"
    }, this.error) : null, m("div", {
      className: "VProj-actions"
    }, m(Button, {
      className: "Button Button--primary",
      type: "submit",
      loading: this.saving
    }, this.p ? 'Save' : 'Start the project'), m(Button, {
      className: "Button",
      onclick: go(this.p ? '/projects/' + this.p.slug : '/projects')
    }, "Cancel")));
  }
}
flarum.reg.add('voucha-community', 'forum/projects/ProjectForm', ProjectForm);